// components/portal/SessionRow.tsx
import { memo, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import clsx from 'clsx';
import {
  ChevronRight,
  MessageSquare,
  Box,
  Tag,
  Building2,
  AlertCircle,
} from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import type { ChatSession } from '@/types/types';
import { useKernelStore } from '@/kernel/kernelStores';

const translations = {
  en: {
    justNow: 'Just now',
    untitledSession: 'Untitled Session',
  },
  fr: {
    justNow: "A l'instant",
    untitledSession: 'Session sans titre',
  },
};

const iconMap = {
  Property: <Building2 size={10} className="text-blue-400" />,
  Brand: <Tag size={10} className="text-amber-400" />,
  Unit: <Box size={10} className="text-emerald-400" />,
};

const colorMap = {
  Property: 'bg-blue-500/10 border-blue-500/20 text-blue-200',
  Brand: 'bg-amber-500/10 border-amber-500/20 text-amber-200',
  Unit: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200',
};

const fallbackIcon = <MessageSquare size={10} className="text-white/40" />;

const ContextChip = memo(({ anchor }: { anchor: any }) => {
  const type = typeof anchor?.entityType === 'string' ? anchor.entityType : '';

  return (
    <div
      className={clsx(
        'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-all hover:scale-105',
        colorMap[type as keyof typeof colorMap] || 'border-white/10 bg-white/5 text-white/70'
      )}
    >
      {iconMap[type as keyof typeof iconMap] ?? fallbackIcon}
      <span className="max-w-[100px] truncate">{anchor?.name ?? anchor?.entityId ?? 'Context'}</span>
    </div>
  );
});
ContextChip.displayName = 'ContextChip';

export const SessionRow = memo(({
  session,
  index,
  isWaiting,
  compact = false,
}: {
  session: ChatSession;
  index?: number;
  isWaiting?: boolean;
  compact?: boolean;
}) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const updatedAgo = session.updatedAt
    ? formatDistanceToNow(new Date(session.updatedAt), { addSuffix: true, locale: lang === 'fr' ? fr : undefined })
    : t.justNow;

  const handleOpen = useCallback(() => {
    kernel.current?.activateChatSession(session.id);
  }, [kernel, session.id]);

  const messageCount = Math.max(session.messageCount ?? 0, 0);
  const lastMessageSnippet =
    typeof session.lastMessageSnippet === 'string' && session.lastMessageSnippet.trim().length > 0
      ? session.lastMessageSnippet
      : null;

  return (
    <button
      type="button"
      onClick={handleOpen}
      className={clsx(
        'group relative flex w-full items-center text-left transition-all duration-300',
        'bg-white/[0.02] backdrop-blur-md hover:bg-white/[0.05]',
        isWaiting && 'border-amber-500/50 bg-amber-500/5',
        compact ? 'gap-3 rounded-xl border p-3' : 'gap-6 rounded-2xl border p-4'
      )}
      data-session-row
      data-session-index={index}
    >
      {/* Context Zone */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <h3 className={clsx("truncate font-semibold text-white/90 group-hover:text-white", compact ? "text-xs" : "text-sm")}>
            {session.name || t.untitledSession}
          </h3>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {session.contextSummary?.slice(0, compact ? 2 : 3).map((anchor: any, chipIndex: number) => (
            <ContextChip key={`${anchor?.entityType ?? 'ctx'}-${anchor?.entityId ?? chipIndex}`} anchor={anchor} />
          ))}
          {(session.contextSummary?.length ?? 0) > (compact ? 2 : 3) && (
            <span className="self-center text-[10px] text-white/30">+{(session.contextSummary?.length ?? 0) - (compact ? 2 : 3)}</span>
          )}
        </div>

        {lastMessageSnippet && (
          <p className="truncate text-xs italic text-white/40 group-hover:text-white/60">
            "{lastMessageSnippet}"
          </p>
        )}
      </div>

      {/* Activity Zone */}
      <div className={clsx("flex items-center", compact ? "gap-2" : "gap-6 pr-2")}>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-3">
            {isWaiting && (
              <div className="flex items-center gap-1 text-amber-400">
                <AlertCircle size={12} />
                {!compact && <span className="text-[10px] font-bold">Needs Input</span>}
              </div>
            )}
            {!compact && (
              <div className="flex items-center gap-1 font-mono text-[11px] text-white/40">
                <MessageSquare size={12} />
                {messageCount}
              </div>
            )}
          </div>
          <span className="text-[10px] text-white/20">{updatedAgo}</span>
        </div>

        {!compact && (
          <ChevronRight
            size={18}
            className="text-white/10 transition-all group-hover:translate-x-1 group-hover:text-white/40"
          />
        )}
      </div>
    </button>
  );
});
SessionRow.displayName = 'SessionRow';
